import { useEffect, useMemo, useState } from 'react'
import { WalletContext, type Connection, type WalletState } from './walletContext'

function getSolanaProvider(): SolanaWalletProvider | undefined {
  return window.phantom?.solana ?? window.backpack?.solana ?? window.solflare ?? window.solana
}

async function connectEvmInternal(): Promise<Connection> {
  const eth = window.ethereum
  if (!eth) throw new Error('no_evm_wallet')
  const accounts = (await eth.request({ method: 'eth_requestAccounts' })) as unknown
  const list = Array.isArray(accounts) ? (accounts as unknown[]).map(String) : []
  const address = list[0]
  if (!address) throw new Error('no_accounts')
  const chainId = (await eth.request({ method: 'eth_chainId' })) as unknown
  return { kind: 'evm', address, chainId: typeof chainId === 'string' ? chainId : undefined }
}

async function connectSolanaInternal(): Promise<Connection> {
  const sol = getSolanaProvider()
  if (!sol) throw new Error('no_solana_wallet')
  await sol.connect()
  const address = sol.publicKey?.toString?.()
  if (!address) throw new Error('no_public_key')
  return { kind: 'solana', address }
}

async function disconnectInternal(connection: Connection | null) {
  if (!connection) return
  if (connection.kind === 'evm') return
  const sol = getSolanaProvider()
  if (!sol) return
  try {
    await sol.disconnect()
  } catch (_e) {
    return
  }
}

export function WalletProvider(props: { children: React.ReactNode }) {
  const [connection, setConnection] = useState<Connection | null>(null)

  const hasEvm = Boolean(window.ethereum)
  const hasSolana = Boolean(getSolanaProvider())

  useEffect(() => {
    const eth = window.ethereum
    if (!eth?.on) return

    const onAccountsChanged = (accounts: unknown) => {
      const list = Array.isArray(accounts) ? (accounts as unknown[]).map(String) : []
      const next = list[0]
      if (!next) {
        setConnection(null)
        return
      }
      setConnection((prev) => (prev?.kind === 'evm' ? { ...prev, address: next } : prev))
    }

    const onChainChanged = (chainId: unknown) => {
      if (typeof chainId !== 'string') return
      setConnection((prev) => (prev?.kind === 'evm' ? { ...prev, chainId } : prev))
    }

    eth.on('accountsChanged', onAccountsChanged)
    eth.on('chainChanged', onChainChanged)
    return () => {
      eth.removeListener?.('accountsChanged', onAccountsChanged)
      eth.removeListener?.('chainChanged', onChainChanged)
    }
  }, [])

  const value = useMemo<WalletState>(
    () => ({
      connection,
      hasEvm,
      hasSolana,
      connectEvm: async () => {
        const c = await connectEvmInternal()
        setConnection(c)
      },
      connectSolana: async () => {
        const c = await connectSolanaInternal()
        setConnection(c)
      },
      disconnect: async () => {
        await disconnectInternal(connection)
        setConnection(null)
      },
    }),
    [connection, hasEvm, hasSolana],
  )

  return <WalletContext.Provider value={value}>{props.children}</WalletContext.Provider>
}
